import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/Footer';

const { FiArrowRight, FiBox, FiLayers, FiMaximize } = FiIcons;

const categories = [
  { id: 'deskove', label: 'DESKOVÉ PANELY', icon: FiLayers },
  { id: 'zebrovane', label: 'ŽEBROVANÉ PANELY', icon: FiBox },
  { id: 'vip', label: 'VIP PANELY', icon: FiMaximize },
  { id: 'dvere', label: 'DVEŘE', icon: FiBox },
  { id: 'metalove', label: 'METALOVÉ PROFILY', icon: FiLayers },
  { id: 'kamenne', label: 'KAMENNÉ DESKY', icon: FiMaximize }
];

const productsData = {
  deskove: [
    {
      id: 1,
      title: 'WPC Stěnový Panel - Standard',
      description: 'Základní řada interiérových WPC panelů s koextrudovanou krystalickou vrstvou a bambusovým jádrem.',
      image: 'https://media-manager-c.questera.ai/greta-media/6f193b2d92211423257b0e3c0dceb84ada06e24e969d96db08b6a1796096f07796f46a52af0758ac75a61f230dd98906/images/aW1hZ2UvcG5n/04213fc51043499c78e9ebec861dd765.png',
      tags: ['Tloušťka 5/8/12mm', 'Třída B1'],
      color: 'text-brand-500',
      borderColor: 'group-hover:border-brand-500'
    }
  ],
  zebrovane: [
    {
      id: 2,
      title: 'WPC Rýhovaný Profil',
      description: 'Designové rýhované profily pro vytváření 3D struktury na stěnách a stropech. Ideální pro moderní interiéry.',
      image: 'https://media-manager-c.questera.ai/greta-media/6f193b2d92211423257b0e3c0dceb84ada06e24e969d96db08b6a1796096f07796f46a52af0758ac75a61f230dd98906/images/aW1hZ2UvcG5n/bb3e9a0cbd611b7a752e63ae9bcea4b0.png',
      tags: ['3000mm délka', '3D efekt'],
      color: 'text-accent-teal',
      borderColor: 'group-hover:border-accent-teal'
    }
  ],
  vip: [
    {
      id: 3,
      title: 'Akustický Panel PRO',
      description: 'Panely navržené pro maximální pohlcování zvuku. Snižují ozvěnu a zlepšují akustiku v komerčních i rezidenčních prostorech.',
      image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&q=80',
      tags: ['Absorpce hluku', 'Designový prvek'],
      color: 'text-accent-sage',
      borderColor: 'group-hover:border-accent-sage'
    }
  ],
  dvere: [],
  metalove: [],
  kamenne: [
    {
      id: 4,
      title: 'Soft Stone - Ohebný kámen',
      description: 'Inovativní obkladový materiál imitující přírodní kámen s extrémní flexibilitou a nízkou hmotností.',
      image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?auto=format&fit=crop&q=80',
      tags: ['Flexibilní', 'Lehký materiál'],
      color: 'text-accent-clay',
      borderColor: 'group-hover:border-accent-clay'
    }
  ]
};

const Products = () => {
  const [activeCategory, setActiveCategory] = useState('deskove');
  const currentProducts = productsData[activeCategory] || [];

  return (
    <motion.div 
      className="min-h-screen bg-arch-light dark:bg-dark-bg text-arch-dark dark:text-gray-200 transition-colors duration-300"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <Header />
      
      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          
          {/* Header Section */}
          <header className="mb-16 border-b border-arch-border dark:border-dark-border pb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-8 h-[1px] bg-brand-500"></div>
                <span className="text-xs font-bold text-arch-gray dark:text-gray-400 uppercase tracking-widest">
                  Naše portfolio
                </span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold tracking-tight mb-6 text-arch-dark dark:text-white">
                Produktové řady.
              </h1>
              <p className="text-arch-gray dark:text-gray-400 max-w-2xl text-lg leading-relaxed font-light">
                Objevte naši kompletní nabídku inovativních stavebních a designových materiálů. 
                Od prémiových WPC panelů až po akustická řešení a ohebný kámen.
              </p>
            </motion.div>
          </header>

          {/* Category Navigation */}
          <div className="flex flex-wrap border border-arch-border dark:border-dark-border mb-16 w-full bg-white dark:bg-dark-surface shadow-sm">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex-1 min-w-[160px] flex items-center justify-center space-x-3 px-6 py-5 text-xs font-bold tracking-widest uppercase transition-all duration-300 border-b md:border-b-0 md:border-r last:border-0 border-arch-border dark:border-dark-border
                  ${activeCategory === cat.id 
                    ? 'bg-arch-dark text-white dark:bg-white dark:text-arch-dark shadow-inner' 
                    : 'text-arch-gray dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-brand-500 dark:hover:text-brand-500'
                  }`}
              >
                <SafeIcon icon={cat.icon} className="w-4 h-4" />
                <span>{cat.label}</span>
              </button>
            ))}
          </div>

          {/* Product Grid */}
          <div className="min-h-[50vh]">
            <AnimatePresence mode="wait">
              <motion.div 
                key={activeCategory}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {currentProducts.map((product, idx) => (
                  <div 
                    key={product.id}
                    className={`group bg-white dark:bg-dark-surface border border-arch-border dark:border-dark-border overflow-hidden transition-all duration-500 hover:shadow-2xl ${product.borderColor}`}
                  >
                    {/* Product Image */}
                    <div className="aspect-[4/3] overflow-hidden bg-arch-light dark:bg-[#1A1D24] relative">
                      <div className="absolute inset-0 bg-black/5 group-hover:bg-black/0 transition-colors z-10"></div>
                      <img 
                        src={product.image} 
                        alt={product.title} 
                        className="w-full h-full object-cover mix-blend-multiply dark:mix-blend-normal group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    
                    {/* Product Content */}
                    <div className="p-8">
                      <div className="flex flex-wrap gap-2 mb-4">
                        {product.tags.map((tag, i) => (
                          <span 
                            key={i} 
                            className="bg-arch-light dark:bg-[#1A1D24] border border-arch-border dark:border-dark-border px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-arch-gray dark:text-gray-400"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      
                      <h3 className="text-xl font-bold text-arch-dark dark:text-white mb-3 tracking-tight">
                        {product.title}
                      </h3>
                      
                      <p className="text-sm text-arch-gray dark:text-gray-400 leading-relaxed mb-8 h-20 overflow-hidden">
                        {product.description}
                      </p>
                      
                      <a 
                        href={`#/specifications`} 
                        className={`inline-flex items-center space-x-2 text-xs font-bold uppercase tracking-widest transition-colors ${product.color} group-hover:text-arch-dark dark:group-hover:text-white`}
                      >
                        <span>Technická data</span>
                        <SafeIcon icon={FiArrowRight} className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </a>
                    </div>
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>
            
            {currentProducts.length === 0 && (
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                className="py-32 flex flex-col items-center justify-center text-center border border-dashed border-arch-border dark:border-dark-border bg-white/50 dark:bg-dark-surface/50"
              >
                <div className="w-16 h-16 mb-6 text-arch-border dark:text-dark-border">
                  <SafeIcon icon={FiBox} className="w-16 h-16" />
                </div>
                <h3 className="text-xl font-bold text-arch-dark dark:text-white mb-2">Portfolio se připravuje</h3>
                <p className="text-arch-gray dark:text-gray-500 font-mono text-sm max-w-md">
                  Produkty pro tuto kategorii budou brzy doplněny do našeho online katalogu.
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </motion.div>
  );
};

export default Products;